import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import type { TrendFilters } from './types'

type Props = {
  filters: TrendFilters
  total: number
  selectedCount: number
  onFiltersChange: (filters: TrendFilters) => void
  onBulkDelete: () => void
}

const STATUS_OPTIONS = [
  { label: 'ทุกสถานะ', value: 'all' },
  { label: 'พร้อมใช้งาน', value: 'active' },
  { label: 'กำลังประมวลผล', value: 'processing' },
  { label: 'ประมวลผลล้มเหลว', value: 'failed' },
  { label: 'ซ่อน', value: 'hidden' },
  { label: 'ลบแล้ว', value: 'deleted' },
  { label: 'ถูกแบน', value: 'banned' },
] as const

export default function TrendsFilter({
  filters,
  total,
  selectedCount,
  onFiltersChange,
  onBulkDelete,
}: Props) {
  const updateFilters = (updates: Partial<TrendFilters>) => {
    onFiltersChange({
      ...filters,
      ...updates,
      page: 1,
    })
  }

  const clearFilters = () => {
    onFiltersChange({
      ...filters,
      page: 1,
      status: '',
      deleted: 'false',
      startDate: '',
      endDate: '',
    })
  }

  const handleDeletedChange = (deleted: 'false' | 'true') => {
    updateFilters({
      deleted,
      status: deleted === 'true' ? '' : filters.status,
    })
  }

  return (
    <Card>
      <CardContent>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-1">
              <h2 className="text-lg font-semibold text-black">
                จัดการคลิปเทรนด์
              </h2>
              <p className="text-sm text-muted-foreground">
                {total} คลิปเทรนด์ในระบบ
              </p>
            </div>
            <Button
              variant="destructive"
              disabled={selectedCount === 0}
              onClick={onBulkDelete}
            >
              ลบที่เลือก ({selectedCount})
            </Button>
          </div>

          <Separator />

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">สถานะลบ</label>
              <Select
                value={filters.deleted}
                onValueChange={(value) =>
                  handleDeletedChange(value as 'false' | 'true')
                }
              >
                <SelectTrigger className="w-[170px]">
                  <SelectValue placeholder="ยังไม่ลบ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="false">ยังไม่ลบ</SelectItem>
                  <SelectItem value="true">Soft-del=true</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">สถานะการประมวลผล</label>
              <Select
                value={filters.status || 'all'}
                disabled={filters.deleted === 'true'}
                onValueChange={(value) =>
                  updateFilters({ status: value === 'all' ? '' : value })
                }
              >
                <SelectTrigger className="w-[190px]">
                  <SelectValue placeholder="ทุกสถานะ" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.filter(
                    (option) => option.value !== 'deleted',
                  ).map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">วันที่เริ่มต้น</label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(event) =>
                  updateFilters({ startDate: event.target.value })
                }
                className="w-[170px]"
              />
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">วันที่สิ้นสุด</label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(event) =>
                  updateFilters({ endDate: event.target.value })
                }
                className="w-[170px]"
              />
            </div>

            <Button variant="outline" onClick={clearFilters}>
              ล้างตัวกรอง
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
